import { EventEmitter, inject, Injectable, OnDestroy } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { DfEventService } from '@df/core/service/df-event';
import { Logger } from '@df/core/service/logger';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { Subscription } from 'rxjs/internal/Subscription';

/**
 * An example of an injectable service.
 */
@Injectable({
    providedIn: 'root'
})
export class IdlePingService implements OnDestroy {
    private pingInterval = 0;
    private pingHandle?: number;
    private idlePingInterval = 0;
    private idlePingHandle?: number;
    private subscriptions = new Subscription();

    /**
     * An event emitted when the service is pinging.
     */
    onPing$ = new EventEmitter<void>();

    /**
     * An event emitted when the service is idle pinging.
     */
    onIdlePing$ = new EventEmitter<void>();

    protected logger = inject(Logger);
    protected dfEventService = inject(DfEventService);
    protected timeoutService = injectTimeoutService();

    constructor(
        protected customer: Customer,
        protected requestService: RequestService
    ) {
        this.dfEventService.success(event => {
            if (event.id === 'dfApiRequest') {
                const error = event.data.requestData?.meta?.result?.error;

                if (error && error === 'LOGGEDOUT') {
                    this.customer.model.reset();
                }
            }
        });
    }

    /**
     * Performs any cleanup tasks when Angular destroys the instance.
     */
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();

        this.stop();
        this.idleStop();
    }

    onPing() {
        this.onPing$.emit();
    }

    onIdlePing() {
        this.onIdlePing$.emit();

        this.customer.model.load(true);
    }

    /**
     * Sets the interval (in seconds) at which the ping operation will occur when start() is called.
     * @param seconds - The ping interval in seconds.
     * @return The current interval value.
     */
    interval(seconds?: number): number {
        if (seconds && !isNaN(seconds) && seconds > 0) {
            this.pingInterval = seconds;
        } else if (seconds && !isNaN(seconds) && seconds <= 0) {
            throw new Error('Interval value must be greater than zero.');
        }

        this.logger.log(`Ping interval is ${this.pingInterval} seconds.`);

        return this.pingInterval;
    }

    /**
     * Sets the interval (in seconds) at which the ping operation will occur when start() is called.
     * @param seconds - The ping interval in seconds.
     * @return The current interval value.
     */
    idleInterval(seconds?: number): number {
        if (seconds && !isNaN(seconds) && seconds > 0) {
            this.idlePingInterval = seconds;
        } else if (seconds && !isNaN(seconds) && seconds <= 0) {
            throw new Error('Idle interval value must be greater than zero.');
        }

        this.logger.log(`Idle ping interval is ${this.idlePingInterval} seconds.`);

        return this.idlePingInterval;
    }

    /**
     * Immediately performs the ping operation.
     */
    ping(): void {
        this.onPing();

        this.logger.info(`Ping at ${new Date()}.`);
    }

    /**
     * Immediately performs the ping operation.
     */
    idlePing(): void {
        this.onIdlePing();

        this.logger.info(`Idle ping at ${new Date()}.`);
    }

    /**
     * Starts pinging on an interval.
     */
    start(): void {
        this.stop();

        this.logger.warn(`Ping started at ${new Date()}.`);

        this.timeoutService.platformSafe(() => {
            this.pingHandle = this.timeoutService.setInterval(() => {
                this.ping();
            }, this.pingInterval * 1000);
        }, false);
    }

    /**
     * Starts pinging on an interval.
     */
    idleStart(): void {
        this.idleStop();

        this.logger.warn(`Idle ping started at ${new Date()}.`);

        this.timeoutService.platformSafe(() => {
            this.idlePingHandle = this.timeoutService.setInterval(() => {
                this.idlePing();
            }, this.idlePingInterval * 1000);
        }, false);
    }

    /**
     * Stops pinging on an interval.
     */
    stop(): void {
        if (this.hasPingHandle() && this.pingHandle !== null) {
            clearInterval(this.pingHandle);
            this.pingHandle = undefined;
        }
    }

    /**
     * Stops pinging on an interval.
     */
    idleStop(): void {
        if (this.hasIdlePingHandle() && this.idlePingHandle !== null) {
            clearInterval(this.idlePingHandle);
            this.idlePingHandle = undefined;
        }
    }

    /**
     * Returns whether or not the service will ping automatically at the specified interval.
     * @return True if the service will ping at the specified interval; otherwise, false.
     */
    isRunning(): boolean {
        return this.hasPingHandle();
    }

    /**
     * Returns whether or not the service will ping automatically at the specified interval.
     * @return True if the service will ping at the specified interval; otherwise, false.
     */
    isIdleRunning(): boolean {
        return this.hasIdlePingHandle();
    }

    private hasPingHandle(): boolean {
        return this.pingHandle !== null && typeof this.pingHandle !== 'undefined';
    }

    private hasIdlePingHandle(): boolean {
        return this.idlePingHandle !== null && typeof this.idlePingHandle !== 'undefined';
    }
}
